import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { resolveGraphQLEndpoint } from './graphql-endpoint.js';

function updateMap(latitude, longitude) {
    map.flyTo({
        center: [longitude, latitude],
        essential: true, // Dit zorgt voor een vloeiende overgang
        zoom: 14
    });

    // Update verborgen velden met latitude en longitude waarden
    document.getElementById('latitude').value = latitude;
    document.getElementById('longitude').value = longitude;

    // Als je een marker op de kaart hebt, update deze dan ook
    if (window.propertyMarker) {
        window.propertyMarker.setLngLat([longitude, latitude]);
    } else {
        window.propertyMarker = new mapboxgl.Marker()
            .setLngLat([longitude, latitude])
            .addTo(map);
    }
}


// Function to perform the GraphQL query
const graphqlEndpoint = resolveGraphQLEndpoint();

async function searchAddress(query) {
    const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({
            query: `query SearchAddress($input: String!) {
            searchAddress(input: $input) {
                address
                street
                houseNumber
                zipCode
                municipality
                latitude
                longitude
                capakey
            }
        }`,
            variables: { input: query }
        })
    });

    const data = await response.json();
    return data.data.searchAddress.map(item => item);
}


// =====================================================================
//  Perceeloppervlakte ophalen uit het kadaster (GRB van Geopunt)
//  Enkel voor huizen. Wordt als suggestie getoond bij stap 2.
// =====================================================================

// In dev (localhost) lopen de geo-calls via een same-origin Vite-proxy
// (zie geoApiProxyPlugin in vite.config.js); in productie rechtstreeks naar
// de Vlaamse API's, die CORS toelaten.
function isLocalHostname() {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
        || (typeof h === 'string' && h.endsWith('.local'));
}
const GRB_WFS_ENDPOINT = isLocalHostname() ? '/api/geo/grb-wfs' : 'https://geo.api.vlaanderen.be/GRB/wfs';
const GRB_WMS_ENDPOINT = isLocalHostname() ? '/api/geo/grb-wms' : 'https://geo.api.vlaanderen.be/GRB/wms';
const BASISREGISTERS_ENDPOINT = isLocalHostname() ? '/api/geo/basisregisters' : 'https://api.basisregisters.vlaanderen.be';

// Oppervlakte van een Lambert 72-ring (m²) via de schoenveterformule.
function ringArea(ring) {
    let sum = 0;
    for (let i = 0; i < ring.length - 1; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[i + 1];
        sum += x1 * y2 - x2 * y1;
    }
    return Math.abs(sum) / 2;
}

// Netto-oppervlakte van een (Multi)Polygon: buitenring min eventuele gaten.
function geometryArea(geometry) {
    if (!geometry) return 0;
    let polygons = [];
    if (geometry.type === 'Polygon') {
        polygons = [geometry.coordinates];
    } else if (geometry.type === 'MultiPolygon') {
        polygons = geometry.coordinates;
    } else {
        return 0;
    }
    let total = 0;
    polygons.forEach(function (poly) {
        if (!poly || !poly.length) return;
        total += ringArea(poly[0]);
        for (let h = 1; h < poly.length; h++) {
            total -= ringArea(poly[h]);
        }
    });
    return total;
}

// Primaire route: we kennen de capakey al uit searchAddress -> 1 call.
// Het GRB WFS-perceel komt in Lambert 72 (meters), dus de oppervlakte
// volgt rechtstreeks uit de geometrie.
async function fetchParcelAreaByCapakey(capakey) {
    if (!capakey) return null;
    const url = GRB_WFS_ENDPOINT + '?service=WFS&version=2.0.0&request=GetFeature'
        + '&typeNames=GRB:ADP&outputFormat=application/json&count=1'
        + '&CQL_FILTER=' + encodeURIComponent("CAPAKEY='" + capakey + "'");
    try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const data = await res.json();
        const feature = data.features && data.features[0];
        if (!feature) return null;
        const area = geometryArea(feature.geometry);
        return area > 0 ? Math.round(area) : null;
    } catch (e) {
        return null;
    }
}

// Fallback wanneer er geen capakey is: adres -> Lambert 72-coördinaten
// (Basisregisters) -> perceel onder het punt (WMS GetFeatureInfo, OPPERVL).
async function fetchParcelAreaByAddress(street, houseNumber, municipality) {
    if (!street || !houseNumber || !municipality) return null;
    try {
        const listUrl = BASISREGISTERS_ENDPOINT + '/v2/adressen?'
            + new URLSearchParams({ straatnaam: street, huisnummer: houseNumber, gemeentenaam: municipality });
        const listRes = await fetch(listUrl, { headers: { Accept: 'application/json' } });
        if (!listRes.ok) return null;
        const list = await listRes.json();
        const objectId = list.adressen && list.adressen[0]
            && list.adressen[0].identificator && list.adressen[0].identificator.objectId;
        if (!objectId) return null;

        // Detail via objectId (zodat ook dev via de proxy loopt; de absolute
        // "detail"-URL uit de respons zou anders cross-origin zijn).
        const detailUrl = BASISREGISTERS_ENDPOINT + '/v2/adressen/' + objectId;
        const detailRes = await fetch(detailUrl, { headers: { Accept: 'application/json' } });
        if (!detailRes.ok) return null;
        const detail = await detailRes.json();
        const gml = detail.adresPositie && detail.adresPositie.geometrie && detail.adresPositie.geometrie.gml;
        const match = gml && gml.match(/<gml:pos>([-\d.]+)\s+([-\d.]+)<\/gml:pos>/);
        if (!match) return null;
        const x = parseFloat(match[1]);
        const y = parseFloat(match[2]);

        const d = 50; // halve bbox in meters -> virtueel 101x101 beeld, klik in het midden
        const bbox = [x - d, y - d, x + d, y + d].join(',');
        const wmsUrl = GRB_WMS_ENDPOINT + '?service=WMS&version=1.3.0&request=GetFeatureInfo'
            + '&layers=GRB_ADP&query_layers=GRB_ADP&crs=EPSG:31370'
            + '&bbox=' + bbox + '&width=101&height=101&i=50&j=50'
            + '&info_format=application/json&feature_count=1';
        const wmsRes = await fetch(wmsUrl, { headers: { Accept: 'application/json' } });
        if (!wmsRes.ok) return null;
        const wms = await wmsRes.json();
        const props = wms.features && wms.features[0] && wms.features[0].properties;
        const oppervl = props && props.OPPERVL;
        return oppervl > 0 ? Math.round(oppervl) : null;
    } catch (e) {
        return null;
    }
}

// Cache de (in-flight) belofte per adres zodat we niet bij elke
// her-render opnieuw fetchen.
const parcelAreaCache = {};
function getParcelArea() {
    const capakey = sessionStorage.getItem('capakey') || '';
    const street = sessionStorage.getItem('street') || '';
    const houseNumber = sessionStorage.getItem('houseNumber') || '';
    const municipality = sessionStorage.getItem('municipality') || '';
    const cacheKey = capakey || (street + '|' + houseNumber + '|' + municipality);
    if (!cacheKey.replace(/\|/g, '')) return Promise.resolve(null);

    if (!(cacheKey in parcelAreaCache)) {
        parcelAreaCache[cacheKey] = (async function () {
            const byCapakey = await fetchParcelAreaByCapakey(capakey);
            if (byCapakey) return byCapakey;
            return await fetchParcelAreaByAddress(street, houseNumber, municipality);
        })();
    }
    return parcelAreaCache[cacheKey];
}

// Toon (of verberg) de suggestie onder het perceelveld.
async function loadParcelSuggestion() {
    const container = document.getElementById('surfacePlotSuggestion');
    if (!container) return;

    const propertyType = document.getElementById('propertyType').value;
    const capakey = sessionStorage.getItem('capakey') || '';
    const hasAddress = sessionStorage.getItem('street') || capakey;

    if (propertyType !== 'HOUSE' || !hasAddress) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }

    container.classList.remove('hidden');
    container.innerHTML = '<p class="flex items-center gap-2 text-gray-500">'
        + '<span class="inline-block w-3.5 h-3.5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></span>'
        + 'Perceeloppervlakte ophalen uit het kadaster…</p>';

    const requestedKey = capakey || sessionStorage.getItem('street');
    const area = await getParcelArea();

    // Adres of type kan ondertussen veranderd zijn -> negeer verouderd resultaat.
    const currentKey = (sessionStorage.getItem('capakey') || '') || sessionStorage.getItem('street');
    if (document.getElementById('propertyType').value !== 'HOUSE' || currentKey !== requestedKey) {
        return;
    }

    if (!area) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }

    renderParcelSuggestion(area);
}

function renderParcelSuggestion(area) {
    const container = document.getElementById('surfacePlotSuggestion');
    const input = document.getElementById('surfacePlot');
    if (!container || !input) return;

    // Vul automatisch in wanneer de gebruiker nog niets heeft ingevuld.
    if (String(input.value).trim() === '') {
        input.value = area;
        clearError(input);
    }
    const applied = String(input.value).trim() === String(area);

    container.classList.remove('hidden');
    container.innerHTML =
        '<p class="flex items-center gap-1.5 text-xs text-gray-500">'
        + '<svg class="w-3.5 h-3.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">'
        + '<path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"/>'
        + '<path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>'
        + '</svg>'
        + '<span>Kadaster (GRB): <span class="font-medium text-gray-700">± ' + area + ' m²</span>'
        + (applied
            ? ' ingevuld — pas gerust aan.</span>'
            : '. <button type="button" id="useParcelSuggestion" class="font-medium text-blue-600 underline hover:text-blue-800">Gebruik</button></span>')
        + '</p>';

    const btn = document.getElementById('useParcelSuggestion');
    if (btn) {
        btn.addEventListener('click', function () {
            input.value = area;
            clearError(input);
            renderParcelSuggestion(area);
        });
    }
}


let currentSuggestions = [];

// Function to show suggestions
function showSuggestions(suggestions) {

    currentSuggestions = suggestions; // Sla de huidige suggesties op

    activeSuggestionIndex = -1; // Reset de actieve suggestie index
    const suggestionsContainer = document.getElementById('suggestions');
    suggestionsContainer.innerHTML = '';

    suggestions.forEach(item => {
        //console.log(item)   
        const div = document.createElement('div');
        div.className = 'p-2 hover:bg-gray-200 cursor-pointer';
        div.textContent = item.address;
        div.onclick = function () {
            const selectedAddress = {
                address: item.address || '',
                street: item.street || '',
                houseNumber: item.houseNumber || '',
                postalCode: item.zipCode !== null && item.zipCode !== undefined ? String(item.zipCode) : '',
                municipality: item.municipality || '',
                latitude: item.latitude,
                longitude: item.longitude,
                capakey: item.capakey || ''
            };

            document.getElementById('adres').value = selectedAddress.address;
            updateMap(selectedAddress.latitude, selectedAddress.longitude); // Update de kaart
            suggestionsContainer.innerHTML = '';

            // Sla de geselecteerde suggestie op
            sessionStorage.setItem('selectedAddress', selectedAddress.address);
            sessionStorage.setItem('selectedAddressDetails', JSON.stringify(selectedAddress));
            sessionStorage.setItem('street', selectedAddress.street);
            sessionStorage.setItem('houseNumber', selectedAddress.houseNumber);
            sessionStorage.setItem('postalCode', selectedAddress.postalCode);
            sessionStorage.setItem('municipality', selectedAddress.municipality);
            sessionStorage.setItem('capakey', selectedAddress.capakey);
            sessionStorage.setItem('latitude', selectedAddress.latitude);
            sessionStorage.setItem('longitude', selectedAddress.longitude);

            // Haal alvast de perceeloppervlakte op zodra het adres bekend is,
            // zodat de suggestie klaarstaat bij stap 2.
            loadParcelSuggestion();

            // Houd de "Verder naar stap 2"-knop in beeld op mobiel
            // (vroeger scrollde dit naar de kaart eronder, waardoor de knop uit beeld sprong).
            if (window.innerWidth < 768) {
                var nextBtn = document.getElementById('next-button');
                if (nextBtn) nextBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };
        suggestionsContainer.appendChild(div);
    });
}


// Event listener for the address input
document.getElementById('adres').addEventListener('input', async (e) => {
    const query = e.target.value;
    if (query.length > 2) {
        const suggestions = await searchAddress(query);
        showSuggestions(suggestions);
    } else {
        document.getElementById('suggestions').innerHTML = '';
    }
});

let activeSuggestionIndex = -1; // Houdt bij welke suggestie momenteel actief is


document.getElementById('adres').addEventListener('keydown', async (e) => {
    const suggestionsContainer = document.getElementById('suggestions');
    const suggestions = suggestionsContainer.querySelectorAll('div');

    if (e.key === 'ArrowDown') {
        activeSuggestionIndex = (activeSuggestionIndex + 1) % suggestions.length;
        e.preventDefault(); // Voorkom dat de cursor beweegt
        console.log('ArrowDown')
    } else if (e.key === 'ArrowUp') {
        if (activeSuggestionIndex <= 0) {
            activeSuggestionIndex = suggestions.length - 1;
        } else {
            activeSuggestionIndex--;
        }
        e.preventDefault(); // Voorkom dat de cursor beweegt en scrollen
    } else if (e.key === 'Enter') {
        if (activeSuggestionIndex >= 0 && suggestions.length > 0) {
            // Er is een actieve suggestie geselecteerd met de pijltjestoetsen
            suggestions[activeSuggestionIndex].click();
            e.preventDefault(); // Voorkom form submission als een suggestie actief is
        }
    }

    // Markeer de actieve suggestie
    highlightActiveSuggestion(suggestions, activeSuggestionIndex);
});

function highlightActiveSuggestion(suggestions, index) {
    suggestions.forEach((suggestion, i) => {
        if (i === index) {
            suggestion.classList.add('bg-blue-100'); // Highlight stijl
            suggestion.scrollIntoView({ block: 'nearest' });
        } else {
            suggestion.classList.remove('bg-blue-100');
        }
    });
}





document.getElementById('increment').addEventListener('click', function () {
    var input = document.getElementById('bedroomCountNumber');
    input.value = parseInt(input.value, 10) + 1;
});

document.getElementById('decrement').addEventListener('click', function () {
    var input = document.getElementById('bedroomCountNumber');
    var value = parseInt(input.value, 10);
    if (value > 0) {
        input.value = value - 1;
    }
});

const epcToEnergyMapping = {
    'A+': -10,    // Aanname: A+ heeft een specifieke lage waarde die je kunt definiëren
    'A': 50,
    'B': 150,
    'C': 250,
    'D': 350,
    'E': 450,
    'F': 550,   // Pas dit aan op basis van de werkelijke range voor F
};

document.getElementById('epcLabel').addEventListener('change', function () {
    const selectedEpcLabel = this.value;
    const energyInput = document.getElementById('energyConsumptionPerSqm');
    energyInput.value = epcToEnergyMapping[selectedEpcLabel] || ''; // Stel in op de middelste waarde of leeg als niet gevonden
});


// epc waarden
document.getElementById('energyConsumptionPerSqm').addEventListener('input', function () {
    var energyConsumption = this.value;
    var epcLabel = document.getElementById('epcLabel');

    if (energyConsumption < 0) {
        epcLabel.value = 'A+';
    } else if (energyConsumption <= 100) {
        epcLabel.value = 'A';
    } else if (energyConsumption <= 200) {
        epcLabel.value = 'B';
    } else if (energyConsumption <= 300) {
        epcLabel.value = 'C';
    } else if (energyConsumption <= 400) {
        epcLabel.value = 'D';
    } else if (energyConsumption <= 500) {
        epcLabel.value = 'E';
    } else {
        epcLabel.value = 'F';
    }
});
document.getElementById('propertyType').addEventListener('change', updateSurfacePlotVisibility);

function updateSurfacePlotVisibility(resetSurfacePlotValue = true) {
    var propertyType = document.getElementById('propertyType').value;
    var surfacePlotInput = document.getElementById('surfacePlot');
    var surfacePlotContainer = document.getElementById('surfacePlotContainer');
    var buildableAreaContainer = document.getElementById('buildableAreaContainer');

    if (propertyType === 'HOUSE') {
        surfacePlotContainer.style.display = 'block';
        surfacePlotInput.required = true;
        if (buildableAreaContainer) {
            buildableAreaContainer.classList.remove('md:col-span-2');
        }
        if (resetSurfacePlotValue) {
            surfacePlotInput.value = '';
        }
    } else {
        surfacePlotContainer.style.display = 'none';
        surfacePlotInput.required = false;
        surfacePlotInput.value = '';
        if (buildableAreaContainer) {
            buildableAreaContainer.classList.add('md:col-span-2');
        }
    }

    // Toon/verberg de kadaster-suggestie mee met het eigendomstype.
    loadParcelSuggestion();
}

// Vervang 'DOMContentLoaded' met 'pageshow' om zeker te zijn dat de functie wordt uitgevoerd na terugkeer
window.addEventListener('pageshow', function () {
    updateSurfacePlotVisibility(false); // Voer de functie uit zonder de standaardwaarde te resetten
});

// JavaScript om scroll aanpassingen te voorkomen
document.getElementById('surfacePlot').addEventListener('wheel', function (event) {
    event.preventDefault();
});

document.getElementById('buildableArea').addEventListener('wheel', function (event) {
    event.preventDefault();
});

document.getElementById('energyConsumptionPerSqm').addEventListener('wheel', function (event) {
    event.preventDefault();
});

document.getElementById('next-button').addEventListener('click', function (event) {
    event.preventDefault(); // Voorkomt dat de pagina ververst wordt

    var form = document.querySelector('form');
    const adresInput = document.getElementById('adres');
    const inputAddress = adresInput.value;
    const savedAddress = sessionStorage.getItem('selectedAddress');
    const isAddressValid = currentSuggestions.some(item => item.address === inputAddress);

    // Check of het adres overeenkomt met het opgeslagen adres in sessionStorage
    if (inputAddress === savedAddress) {
        // Behandel dit als een geldig geselecteerd adres en ga verder
        document.getElementById('adres-zoekbox').style.display = 'none';
        document.getElementById('vervolg-formulier').style.display = 'block';
        loadParcelSuggestion();
    } else {
        if (!isAddressValid) {
            // Stel een aangepaste foutmelding in en trigger de validatie
            showError(adresInput, 'Selecteer een geldig adres uit de suggesties.'); // Toon de foutmelding met Tailwind
        }

        if (isAddressValid) {
            // Als alles geldig is, ga verder met de logica
            document.getElementById('adres-zoekbox').style.display = 'none';
            document.getElementById('vervolg-formulier').style.display = 'block';

        } else {
            // Forceer de browser om de standaard validatie UI te tonen
            showError(adresInput, 'Selecteer een geldig adres uit de suggesties.'); // Toon de foutmelding met Tailwind
        }
    }
});

function showError(inputElement, message) {
    const id = inputElement.id;
    let messageElement = document.getElementById(id + '-error');
    if (!messageElement) {
        messageElement = document.createElement('div');
        messageElement.id = id + '-error';
        messageElement.className = 'text-red-500 text-sm mt-2';
        inputElement.parentNode.insertBefore(messageElement, inputElement.nextSibling);
    }
    messageElement.textContent = message;
    inputElement.classList.add('border', 'border-red-500');
}

function clearError(inputElement) {
    inputElement.classList.remove('border', 'border-red-500'); // Verwijder fout styling
    let errorElement = document.getElementById(inputElement.id + '-error');
    if (errorElement) {
        errorElement.remove(); // Verwijder de foutmelding
    }
}

// Zorg ervoor dat de foutmelding verdwijnt wanneer de gebruiker begint te typen in het veld
document.getElementById('adres').addEventListener('input', function () {
    clearError(this); // Verwijder de foutmelding en styling voor dit veld
});



document.getElementById('back-button').addEventListener('click', function (event) {
    event.preventDefault(); // Voorkomt dat de pagina ververst wordt

    // Verberg het vervolgformulier
    document.getElementById('vervolg-formulier').style.display = 'none';

    // Toon de adres-zoekbox
    document.getElementById('adres-zoekbox').style.display = 'block';
});
